// Reusable schema.org VideoObject builder (audit C-H2 #3). The site embedded
// YouTube on coaching / datapack / product / blog pages but shipped NO VideoObject
// anywhere. This builds the object from a YouTube URL (or bare id) plus whatever
// metadata the call site can supply, so every embed becomes eligible for video
// rich results.
//
// Kept as a pure, dependency-light util (server-safe, unit-testable) so BOTH the
// current <Youtube> embed component AND the future #118 lite-YouTube facade can
// call the SAME builder — no drift between the interactive embed and the facade.
//
// Emit as a raw <script type="application/ld+json"> (task #119 — next-seo's
// next/head path THROWS under App Router SSR and drops the data).

import getYouTubeId from 'get-youtube-id'

// YouTube video id from any watch / youtu.be / embed URL. Returns null when the
// URL is missing or not a YouTube URL.
export function youTubeIdFromUrl(url) {
  if (!url || typeof url !== 'string') {
    return null
  }
  try {
    return getYouTubeId(url) || null
  } catch {
    return null
  }
}

// Canonical hqdefault thumbnail for a YouTube id (always exists, 480x360).
export function youTubeThumbnailUrl(id) {
  return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : null
}

/**
 * Build a schema.org VideoObject for a YouTube embed.
 *
 * @param {object} args
 * @param {string} [args.url]           YouTube URL (watch / youtu.be / embed)
 * @param {string} [args.id]            YouTube video id (wins over url)
 * @param {string} [args.name]          video title (recommended by Google)
 * @param {string} [args.description]   video description (recommended)
 * @param {string} [args.uploadDate]    ISO 8601 upload date, if known
 * @param {string} [args.thumbnailUrl]  override thumbnail (defaults to hqdefault)
 * @returns {object|null} the VideoObject, or null when no valid id can be derived.
 */
export function buildVideoObjectJsonLd({
  url,
  id,
  name,
  description,
  uploadDate,
  thumbnailUrl,
} = {}) {
  const videoId = id || youTubeIdFromUrl(url)
  if (!videoId) {
    return null
  }
  const obj = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    thumbnailUrl: thumbnailUrl || youTubeThumbnailUrl(videoId),
    embedUrl: `https://www.youtube.com/embed/${videoId}`,
    contentUrl: `https://www.youtube.com/watch?v=${videoId}`,
  }
  if (name) {
    obj.name = name
  }
  if (description) {
    obj.description = description
  }
  if (uploadDate) {
    obj.uploadDate = uploadDate
  }
  return obj
}

// Serialize for embedding in a <script> tag. Escaping '<' -> < prevents a
// string value from prematurely closing the script element (the standard
// JSON-LD-in-<script> robustness guard, mirroring SiteJsonLd.serializeJsonLd).
export function serializeVideoJsonLd(obj) {
  return JSON.stringify(obj).replace(/</g, '\\u003c')
}
